import React, { useState } from "react";
import { Button, Callout } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";

import { ExternalLink } from "@components/ExternalLink";

const TIPS = [
  <p>
    The 'Sem' in Semgrep stands for <em>semantic</em>. This refers to how the
    tool understands the meaning behind the text being searched, instead of just
    naively looking at it as a string of characters.
  </p>,
  <p>
    You can{" "}
    <ExternalLink stayVisible href="/docs/experiments/#autofix">
      add a&nbsp;<code>fix</code>&nbsp;key
    </ExternalLink>{" "}
    to rules to tell Semgrep how to automatically fix your findings! This can
    involve more advanced operations as well, such as replacing one captured
    metavariable with another.
  </p>,
  <p>
    You can quickly ignore any finding by adding a{" "}
    <ExternalLink stayVisible href="/docs/running-rules/#managing-findings">
      &nbsp;<code>{"// nosemgrep"}</code>&nbsp; comment
    </ExternalLink>{" "}
    in the source code. Ignore only one rule by adding its rule ID, such as{" "}
    &nbsp;<code>{"// nosemgrep: bad-func-is-insecure"}</code>.
  </p>,
  <p>
    The Semgrep rule registry on our website is just a frontend for the{" "}
    <ExternalLink
      stayVisible
      href="https://github.com/returntocorp/semgrep-rules"
    >
      returntocorp/semgrep-rules
    </ExternalLink>{" "}
    GitHub repository.
  </p>,
  <p>
    Semgrep has a Visual Studio Code extension to scan your code as you write
    it, and to help you write patterns. You can install it from{" "}
    <ExternalLink
      stayVisible
      href="https://marketplace.visualstudio.com/items?itemName=Semgrep.semgrep"
    >
      the Extension Marketplace
    </ExternalLink>
    !
  </p>,
  <p>
    After matching some code with a Semgrep pattern, you can use{" "}
    <ExternalLink
      stayVisible
      href="/docs/writing-rules/pattern-logic/#metavariable-regex"
    >
      &nbsp;<code>metavariable-regex</code>&nbsp;
    </ExternalLink>{" "}
    to see if the matched part also matches a regular expression.
  </p>,
  <p>
    Rules can contain any arbitrary data under{" "}
    <ExternalLink
      stayVisible
      href="/docs/writing-rules/pattern-logic/#metadata"
    >
      the&nbsp;<code>metadata</code>&nbsp;key
    </ExternalLink>
    . We use this for example to mark the OWASP and CWE vulnerability categories
    that the rule scans for.
  </p>,
  <p>
    The{" "}
    <ExternalLink
      stayVisible
      href="https://github.com/returntocorp/semgrep/tree/develop/semgrep-core"
    >
      parsing &amp; matching core of Semgrep
    </ExternalLink>{" "}
    is implemented in OCaml, which is why it can scan 10,000+ lines/sec. But the{" "}
    <ExternalLink stayVisible href="https://github.com/returntocorp/semgrep">
      <code>semgrep</code>&nbsp;CLI tool
    </ExternalLink>{" "}
    is written in Python to make it easy to contribute to it!
  </p>,
  <p>
    Our Slack community is 1,100+ members strong and growing;{" "}
    <ExternalLink stayVisible href="https://r2c.dev/slack">
      join us
    </ExternalLink>
    to chat with power users and the development team, we are happy to answer
    your questions!
  </p>,
  <p>
    Semgrep is the spiritual successor of a tool called{" "}
    <ExternalLink
      stayVisible
      href="https://en.wikipedia.org/wiki/Coccinelle_(software)"
    >
      <em>Coccinelle</em>
    </ExternalLink>
    , which was initially used to help with refactoring on the Linux kernel. One
    of Coccinelle's authors actually works on Semgrep today!
  </p>,
  <p>
    More than 50 security engineers already contributed to the Semgrep rule
    registry. You can also{" "}
    <ExternalLink
      stayVisible
      href="https://github.com/returntocorp/semgrep-rules"
    >
      submit your own rules on GitHub
    </ExternalLink>
    !
  </p>,
];

export const LoadingTips: React.FC<{}> = () => {
  const [index, setIndex] = useState<number>(
    Math.floor(Math.random() * TIPS.length) // nosem: ajinabraham.njsscan.crypto_node.node_insecure_random_generator
  );
  return (
    <Callout title="Did you know?" icon={IconNames.LIGHTBULB}>
      {TIPS[index]}
      <Button
        onClick={() => {
          setIndex((prevIndex) => (prevIndex + 1) % TIPS.length);
        }}
      >
        Next tip
      </Button>
    </Callout>
  );
};
